// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";

// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import { landingPageImageAtom, signUpPageImageAtom } from "./atoms";
import { validateRequiredEncodedLength } from "helpers/Form/commonFormValidations";
import HeaderImage from "./HeaderImage";
import LandingPagePreview from "./LandingPagePreview";
import { userAtom } from "../../../globalAtoms";
import {
	useCreatePageAssetMutation,
	useCreatePageMutation,
	useEditPageMutation,
	useGetDomainGymQuery,
	useGetPagesQuery,
} from "store/api/api";
import { useParams } from "react-router";
import SignupPagePreview from "./SignupPagePreview";
import { notification, Form, Typography, Input, Flex } from "antd";
import { Button } from "components/Common/Button";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import "./styles.scss";
import { LoadingMask } from "components/Common/LoadingMask";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const MemberPagesForm = (props) => {
	const { gym } = useParams(); //GYM FROM URL PARAMS

	// Should we keep using recoil for this?
	const landingPageImage = useRecoilValue(landingPageImageAtom);
	const signUpPageImage = useRecoilValue(signUpPageImageAtom);
	const setLandingPageImage = useSetRecoilState(landingPageImageAtom);
	const setSignUpPageImage = useSetRecoilState(signUpPageImageAtom);
	const [form] = Form.useForm();
	const [user] = useRecoilState(userAtom);
	const [values, setValues] = useState({});

	// RTKQuery
	const { data: domainGymData, refetch: refetchDomainGymData } = useGetDomainGymQuery(gym, { skip: !gym });
	const gymId = domainGymData?.id || null;
	const { data: pageData, refetch: refetchPages, isLoading } = useGetPagesQuery(gymId, { skip: !gymId });
	const [pages, setPages] = useState([]);
	const [initialValues, setInitialValues] = useState({});
	const [editPageTrigger, editPageResponse] = useEditPageMutation();
	const [addAssetToPageTrigger, addAssetToPageResponse] = useEditPageMutation();
	const [createPageTrigger, createPageResponse] = useCreatePageMutation();
	const [createPageAssetTrigger, createPageAssetResponse] = useCreatePageAssetMutation();
	const primaryColor = usePrimaryColor();

	useEffect(() => {
		if (pageData) {
			setPages(pageData);
			const newValues = pageData.reduce((acc, page) => {
				if (page.type === "sign_up") {
					return {
						...acc,
						signUpSubText: page.sub_text,
						signUpHeaderText: page.header_text,
					};
				}
				if (page.type === "landing") {
					return {
						...acc,
						landingSubText: page.sub_text,
						landingHeaderText: page.header_text,
					};
				}
				return acc;
			}, {});
			setInitialValues((initiaValuesCopy) => {
				return {
					...initiaValuesCopy,
					...newValues,
				};
			});
		}
	}, [pageData]);

	const handleFormSubmit = async (formObj) => {
		// Landing Page Info
		if (
			initialValues.landingHeaderText !== formObj.landingHeaderText ||
			initialValues.landingSubText !== formObj.landingSubText
		) {
			const landingSubmissionObj = {
				header_text: formObj.landingHeaderText,
				sub_text: formObj.landingSubText,
				type: "landing",
				header_image: null,
				gym_id: gymId,
			};
			await submitPageInfo(landingSubmissionObj);
		}
		// Sign Up Info
		if (
			initialValues.signUpHeaderText !== formObj.signUpHeaderText ||
			initialValues.signUpSubText !== formObj.signUpSubText
		) {
			const signUpSubmissionObj = {
				header_text: formObj.signUpHeaderText,
				sub_text: formObj.signUpSubText,
				type: "sign_up",
				header_image: null,
				gym_id: gymId,
			};
			await submitPageInfo(signUpSubmissionObj);
		}
		const { data: pages } = await refetchPages();

		// This is weird, we should probably refactor this
		const uploadPromises = pages.map(async (page) => {
			if (page.type === "sign_up" && signUpPageImage) {
				return await uploadPageAsset(page, signUpPageImage, "sign_up");
			}
			if (page.type === "landing" && landingPageImage) {
				return await uploadPageAsset(page, landingPageImage, "landing");
			}
			return null;
		});
		await Promise.all(uploadPromises);
		//DO WE NEED THIS?
		refetchDomainGymData();
	};

	// If the user doesn't have any pages, create them. Otherwise, edit them.
	const submitPageInfo = async (submissionObj) => {
		try {
			pages.length === 0 ? await createPageTrigger(submissionObj) : await editPageTrigger({ params: submissionObj });
			const { data } = await refetchPages();
			return data;
		} catch (error) {
			console.error("try create/edit page error: ", error);
		}
	};

	useEffect(() => {
		if (createPageResponse.isSuccess) {
			notification.success({
				message: "Page saved successfully",
			});
		} else if (createPageResponse.isError) {
			notification.error({
				message: "Something went wrong saving your page",
			});
		}
	}, [createPageResponse]);

	useEffect(() => {
		if (editPageResponse.isSuccess) {
			notification.success({
				message: "Page saved successfully",
			});
		} else if (editPageResponse.isError) {
			notification.error({
				message: "Something went wrong saving your page",
			});
		}
	}, [editPageResponse]);

	// Upload the image to the server
	const uploadPageAsset = async (page, image, type) => {
		const jwt = localStorage.getItem("creator_jwt_token");
		const formData = new FormData();
		formData.append("image", image);
		formData.append("page_id", page.id);
		formData.append("type", type);
		formData.append("gym_id", domainGymData?.id);
		try {
			await createPageAssetTrigger({ formData, jwt });
			const resetFunc = type === "sign_up" ? setSignUpPageImage : setLandingPageImage;
			resetFunc(null);
		} catch (error) {
			console.error("error: ", error);
			notification.error({
				message: "Something went wrong saving your image",
			});
		}
	};

	// Check for image uploading success or error and notify user
	useEffect(() => {
		if (createPageAssetResponse.isSuccess) {
			notification.success({
				message: "Image saved successfully",
			});
			// If the image was saved successfully, update the page with the location of the image
			if (createPageAssetResponse.data.page_assets.location) {
				const location = createPageAssetResponse.data.page_assets.location;
				const submissionObj = {
					gym_id: domainGymData.id,
					header_text: null,
					sub_text: null,
					header_image: location,
					type: createPageAssetResponse.data.page_assets.page_type,
				};
				addAssetToPageTrigger({ params: submissionObj });
			}
		} else if (createPageAssetResponse.isError) {
			notification.error({
				message: "Something went wrong saving your image",
			});
		}
	}, [createPageAssetResponse, addAssetToPageTrigger, domainGymData.id]);

	useEffect(() => {
		if (addAssetToPageResponse.isSuccess) {
			notification.success({
				message: "Image Added To Page successfully",
			});
			refetchPages();
		} else if (addAssetToPageResponse.isError) {
			notification.error({
				message: "Something went wrong adding page to image",
			});
		}
	}, [addAssetToPageResponse, refetchPages]);

	const landingPageData = pages.find((page) => page.type === "landing");
	useEffect(() => form.resetFields(), [initialValues, form]);
	if (isLoading) return <LoadingMask />;

	return (
		<Form
			className="member-pages"
			form={form}
			initialValues={initialValues}
			onFinish={(formObj) => handleFormSubmit(formObj)}
			layout="vertical"
			hideRequiredMark
			onValuesChange={(_, vals) => setValues(vals)}
		>
			<Typography.Paragraph className="member-page-title">Set up your SWEATBASE Landing Page</Typography.Paragraph>
			<Form.Item
				label="Header Text"
				name="landingHeaderText"
				rules={[
					{
						validator: (_, value) => {
							if (!value) return Promise.reject(new Error("This field is required"));
							const validationMessage = validateRequiredEncodedLength(value, 512);
							if (validationMessage) {
								return Promise.reject(new Error("Header text must be less than 512 characters"));
							}
							return Promise.resolve();
						},
					},
				]}
			>
				<Input placeholder="Header Text" />
			</Form.Item>
			<Form.Item
				label="Sub Text"
				name="landingSubText"
				rules={[
					{
						validator: (_, value) => {
							if (!value) return Promise.reject(new Error("This field is required"));
							const validationMessage = validateRequiredEncodedLength(value, 512);
							if (validationMessage) {
								return Promise.reject(new Error("Sub text must be less than 512 characters"));
							}
							return Promise.resolve();
						},
					},
				]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Form.Item>
				<HeaderImage type="landing" user={user} pageData={pages.find((page) => page.type === "landing")} />
			</Form.Item>
			<Flex className="card" vertical gap={16}>
				<Typography.Title level={4}>Landing Page Example</Typography.Title>
				<Flex
					style={{
						width: "100%",
					}}
					flex={1}
					justify="center"
				>
					<div
						style={{
							width: "100%",
							maxWidth: 512,
							marginBottom: 16,
							background: "#000",
						}}
					>
						<LandingPagePreview
							image={landingPageImage || landingPageData?.header_image}
							color={primaryColor}
							gymName={values.landingHeaderText || landingPageData?.header_text}
							description={values.landingSubText || landingPageData?.sub_text}
						/>
					</div>
				</Flex>
			</Flex>
			<Typography.Paragraph className="member-page-title">Set up your SWEATBASE Sign up Page</Typography.Paragraph>
			<Form.Item
				label="Header Text"
				name="signUpHeaderText"
				rules={[
					{
						validator: (_, value) => {
							if (!value) return Promise.reject(new Error("This field is required"));
							const validationMessage = validateRequiredEncodedLength(value, 512);
							if (validationMessage) {
								return Promise.reject(new Error("Header text must be less than 512 characters"));
							}
							return Promise.resolve();
						},
					},
				]}
			>
				<Input />
			</Form.Item>
			<Form.Item
				label="Sub Text"
				name="signUpSubText"
				rules={[
					{
						validator: (_, value) => {
							if (!value) return Promise.reject(new Error("This field is required"));
							const validationMessage = validateRequiredEncodedLength(value, 512);
							if (validationMessage) {
								return Promise.reject(new Error("Sub text must be less than 512 characters"));
							}
							return Promise.resolve();
						},
					},
				]}
			>
				<Input.TextArea rows={4} />
			</Form.Item>
			<Form.Item>
				<HeaderImage type="sign_up" user={user} pageData={pages.find((page) => page.type === "sign_up")} />
			</Form.Item>
			<Flex className="card" vertical gap={16}>
				<Typography.Title level={4}>Sign up Page Example</Typography.Title>
				<Flex
					style={{
						width: "100%",
					}}
					flex={1}
					justify="center"
				>
					<div style={{ width: "100%", maxWidth: 375, marginBottom: 16 }}>
						<SignupPagePreview
							style={{
								backgroundColor: "#1d2023",
								display: "flex",
								width: "100%",
								height: "100%",
								borderRadius: "5px",
							}}
							image={signUpPageImage || pages.find((page) => page.type === "sign_up")?.header_image}
							type="signUp"
							gym={domainGymData}
							pageData={pages.find((page) => page.type === "sign_up")}
							headerText={values.signUpHeaderText || pages.find((page) => page.type === "sign_up")?.header_text}
							subText={values.signUpSubText || pages.find((page) => page.type === "sign_up")?.sub_text}
						/>
					</div>
				</Flex>
			</Flex>
			<Button
				className="primary-filled"
				loading={
					createPageResponse.isLoading ||
					editPageResponse.isLoading ||
					addAssetToPageResponse.isLoading ||
					createPageAssetResponse.isLoading
				}
				disabled={
					createPageResponse.isLoading ||
					editPageResponse.isLoading ||
					addAssetToPageResponse.isLoading ||
					createPageAssetResponse.isLoading
				}
				style={{ maxWidth: "unset" }}
				type="submit"
			>
				Save Changes
			</Button>
		</Form>
	);
};

export default MemberPagesForm;
